/**
 * Phase 4-D4 VwOOZ — 2FA 备用恢复码页 `/profile/mfa/backup-codes`
 *
 * 路径:`/profile/mfa/backup-codes`(authed)
 * frame:ui/dogdisk-user.pen#VwOOZ(2026-05-21 #4 补画)
 *
 * 与 /profile/mfa 区别:
 *   - /profile/mfa(MfaPage)管 TOTP enable/disable + 内嵌"重新生成备用码"button(legacy
 *     走 POST /gapi/user/auth/2fa/regenerate-recovery-codes;写 supabase app_metadata)
 *   - 本 page(/profile/mfa/backup-codes)独立 VwOOZ frame,走新 endpoint:
 *     POST /gapi/user/auth/mfa/backup-codes/generate(D4 新建,写 public.user_backup_codes 表)
 *
 * UI 行为:
 *   - 首次进入(剩余 count 0):显 "生成备用码" CTA;click → POST generate → 显 10 个码 + actions
 *   - Copy:navigator.clipboard.writeText(codes joined \n)
 *   - Download:trigger a.download .txt blob
 *   - Regenerate:confirm modal(强提示旧码失效)→ 重 POST 覆盖
 *   - warning 黄底 text "使用 1 个备用码后,系统会立即作废它,请妥善保存"
 *
 * 注:本 page 暂未集成 backup-codes/count endpoint(后端可后续扩展),
 *  剩余 count 在 generate 后直接 = 10;若已有 codes 但未 generate,显示 "已生成"。
 *  对照 spec §4.3:VwOOZ 顶部 count 显示是"装饰性",非阻塞 generate flow。
 */
import { useState } from 'react'
import { apiClient, copyToClipboard } from '@dogdisk/shared'
import { MobileScreen, TopBar } from '../../components/mobile-screen'

type GenerateResp = {
  codes: string[]
  count: number
}

export default function BackupCodesPage() {
  const [codes, setCodes] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showConfirm, setShowConfirm] = useState(false)
  const [copiedAt, setCopiedAt] = useState<number | null>(null)

  // 剩余 count(MVP:generate 后 = 10;未 generate = 0;本 page 不主动 fetch count
  // 因为 spec 中未要求新 count endpoint;后续可扩展)
  const remaining = codes.length

  const doGenerate = async (regenerate: boolean) => {
    setBusy(true)
    setError(null)
    setShowConfirm(false)
    try {
      const r = await apiClient.post<GenerateResp>(
        '/gapi/user/auth/mfa/backup-codes/generate',
        {},
      )
      setCodes(r.codes)
      // regenerate 语义在后端体现(DELETE 旧 + INSERT 新);前端仅刷新 state
      void regenerate
    } catch (e) {
      setError(e instanceof Error ? e.message : '生成失败')
    } finally {
      setBusy(false)
    }
  }

  const onCopy = async () => {
    if (codes.length === 0) return
    const ok = await copyToClipboard(codes.join('\n'))
    if (ok) setCopiedAt(Date.now())
    // 复制不可用(非 https / 旧浏览器)— 静默
  }

  const onDownload = () => {
    if (codes.length === 0) return
    const blob = new Blob([codes.join('\n') + '\n'], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `dogdisk-backup-codes-${new Date().toISOString().slice(0, 10)}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <MobileScreen bg="surface">
      <TopBar title="备用恢复码" />
      <main data-frame-id="VwOOZ" data-testid="VwOOZ" className="flex flex-col gap-5 px-6 pt-4">
        <header className="flex flex-col gap-1">
          <h2 className="text-[18px] font-bold text-[#1A1A1A]">2FA 备用恢复码</h2>
          <p data-testid="VwOOZ-remaining" className="text-[12px] text-[#9CA3AF]">
            剩余可用:{remaining} 个
          </p>
        </header>

        {/* warning 黄底文案 */}
        <div
          data-testid="VwOOZ-warning"
          className="rounded-lg bg-[#FFF4D6] px-3 py-2 text-[12px] text-[#8a6d00]"
        >
          使用 1 个备用码后,系统会立即作废它,请妥善保存。
        </div>

        {error && <p className="text-[13px] text-[#FF6B6B]">{error}</p>}

        {codes.length === 0 && (
          <button
            type="button"
            data-testid="VwOOZ-generate-btn"
            disabled={busy}
            onClick={() => void doGenerate(false)}
            className="h-12 rounded-lg bg-[#19caad] text-[15px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
          >
            {busy ? '生成中…' : '生成备用码'}
          </button>
        )}

        {codes.length > 0 && (
          <>
            <section
              data-testid="VwOOZ-codes-list"
              className="grid grid-cols-2 gap-2 rounded-2xl bg-white p-4 font-mono"
            >
              {codes.map((c, i) => (
                <div
                  key={i}
                  data-testid={`VwOOZ-code-${i}`}
                  className="rounded-md bg-[#F6F7F8] px-2 py-1.5 text-center text-[13px] tracking-wider text-[#1A1A1A]"
                >
                  {c}
                </div>
              ))}
            </section>

            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="VwOOZ-copy"
                  onClick={() => void onCopy()}
                  className="h-10 flex-1 rounded-lg bg-[#1A1A1A] text-[13px] font-semibold text-white"
                >
                  {copiedAt ? '已复制 ✓' : '复制'}
                </button>
                <button
                  type="button"
                  data-testid="VwOOZ-download"
                  onClick={onDownload}
                  className="h-10 flex-1 rounded-lg bg-[#1A1A1A] text-[13px] font-semibold text-white"
                >
                  下载
                </button>
              </div>
              <button
                type="button"
                data-testid="VwOOZ-regenerate"
                disabled={busy}
                onClick={() => setShowConfirm(true)}
                className="h-10 rounded-lg bg-[#1971ca] text-[13px] font-semibold text-white disabled:opacity-50"
              >
                重新生成(旧码立即失效)
              </button>
            </div>
          </>
        )}

        {showConfirm && (
          <div
            role="dialog"
            aria-modal="true"
            data-testid="VwOOZ-confirm-regen"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
            onClick={() => setShowConfirm(false)}
          >
            <div
              className="w-full max-w-[320px] rounded-2xl bg-white p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-[15px] font-semibold text-[#1A1A1A]">确认重新生成?</h3>
              <p className="mt-2 text-[12px] text-[#9CA3AF]">
                生成后,当前所有备用码立即失效。请确保新码会被妥善保存。
              </p>
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowConfirm(false)}
                  className="h-10 flex-1 rounded-lg bg-[#F1F2F4] text-[13px] font-semibold text-[#1A1A1A]"
                >
                  取消
                </button>
                <button
                  type="button"
                  data-testid="VwOOZ-confirm-regen-yes"
                  onClick={() => void doGenerate(true)}
                  disabled={busy}
                  className="h-10 flex-1 rounded-lg bg-[#FF6B6B] text-[13px] font-semibold text-white disabled:opacity-50"
                >
                  确认生成
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </MobileScreen>
  )
}
